// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DEFAULT_UNIT_OF_MEASURE } from '@/constants/unit-of-measure';
import { getItems } from '@/items/api/get-items';
import {
  CREATE_ITEM_OPTION_ID,
  ItemAutocomplete,
} from '@/items/components/item-autocomplete';
import type { ItemType } from '@/items/constants/item-types';
import { SparseItem } from '@/sparse/api/types';
import { FilterOptionsState, createFilterOptions } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ForwardedRef, forwardRef } from 'react';

export type ItemFilterCallback = (options: SparseItem[]) => SparseItem[];

const filter = createFilterOptions<SparseItem>();

/**
 * Compares two items for sorting in search results.
 * Items are first grouped by archived status (active first),
 * then sorted alphabetically by name within each group.
 */
function compareItems(a: SparseItem, b: SparseItem) {
  if (a.isArchived === b.isArchived) {
    return a.name.localeCompare(b.name);
  }
  return a.isArchived ? 1 : -1;
}

/**
 * Filters the item options based on the provided filter callback and hidden items.
 *
 * @param items - The full list of items to filter
 * @param filterCallback - Optional callback to apply custom filtering logic
 * @param hiddenItems - Items to exclude from the dropdown
 * @param includeArchived - Whether to include archived items
 * @param physicalOnly - Whether to exclude non-physical items (services)
 */
function getOptions(
  items: SparseItem[] = [],
  filterCallback: ItemFilterCallback | undefined,
  hiddenItems: SparseItem[] | undefined,
  includeArchived: boolean,
  physicalOnly: boolean
) {
  let filteredItems = items;

  // Filter by archived status
  if (!includeArchived) {
    filteredItems = filteredItems.filter((item) => !item.isArchived);
  }

  // Filter by physical status (exclude SERVICE items)
  if (physicalOnly) {
    filteredItems = filteredItems.filter((item) => item.isPhysical);
  }

  // Filter out hidden items
  if (hiddenItems && hiddenItems.length > 0) {
    const hiddenItemIds = new Set(hiddenItems.map((item) => item.id));
    filteredItems = filteredItems.filter((item) => !hiddenItemIds.has(item.id));
  }

  // Apply custom filter callback
  if (filterCallback !== undefined) {
    filteredItems = filterCallback(filteredItems);
  }

  return filteredItems;
}

interface Props {
  /**
   * The name attribute for the form input element.
   */
  name?: string;

  /**
   * The label text displayed above the input element.
   */
  label?: string;

  /**
   * If true, displays the component in an error state with red highlighting.
   */
  error?: boolean;

  /**
   * Text displayed below the input element.
   */
  helperText?: string;

  /**
   * The currently selected item in the search field.
   */
  value: SparseItem | null;

  /**
   * Optional array of items to hide from the dropdown.
   * Items are filtered by ID.
   */
  hiddenItems?: SparseItem[];

  /**
   * If true, the input is disabled.
   */
  disabled?: boolean;

  /**
   * If true, archived items will be shown in the dropdown.
   * Defaults to false.
   */
  includeArchived?: boolean;

  /**
   * If true, only physical items (DEVICE, BULK) will be shown.
   * Non-physical items (SERVICE) will be excluded.
   * Defaults to false.
   */
  physicalOnly?: boolean;

  /**
   * Optional item type filter to show only items of a specific type.
   * If not provided, all item types are shown.
   */
  type?: ItemType;

  /**
   * Optional callback to apply custom filtering logic to the item options.
   */
  filter?: ItemFilterCallback;

  /**
   * Callback fired when an item is selected or deselected.
   */
  onChange: (value: SparseItem | null) => void;

  /**
   * If provided, sets a minimum width for the dropdown popper.
   * Useful when the input field is narrow but options need more space.
   */
  popperWidth?: number;

  /**
   * If true, makes the input field take up full width of its container.
   */
  fullWidth?: boolean;

  /**
   * Placeholder text shown when no value is selected.
   */
  placeholderText?: string;

  /**
   * If true, shows a search icon at the start of the input.
   */
  showSearchAdornment?: boolean;

  /**
   * If set, excludes items already linked to this integration from the results.
   * Also disables the "create new" option in the autocomplete.
   */
  excludeLinkedIntegration?: string;
}

/**
 * Opens the items page in a new tab with a "new" query parameter.
 * This triggers the "Add item" dialog to open with the item name pre-populated.
 */
function openAddItemPage(name: string) {
  const url = new URL('/catalog/items', document.location.origin);
  url.searchParams.append('new', encodeURIComponent(name));
  window.open(url, '_blank');
}

/**
 * Converts an Item from the API to SparseItem format.
 */
function itemToSparseItem(item: {
  id: string;
  key: string;
  name: string;
  sku: string;
  type: 'SERVICE' | 'DEVICE' | 'BULK';
  isArchived: boolean;
  shouldRenderSerialFields: boolean;
}): SparseItem {
  const isPhysical = item.type !== 'SERVICE';
  const hasAssets = item.type === 'DEVICE';

  return {
    id: item.id,
    key: item.key,
    name: item.name,
    type: item.type,
    sku: item.sku,
    isArchived: item.isArchived,
    unitOfMeasure: DEFAULT_UNIT_OF_MEASURE,
    hasAssets,
    isPhysical,
    shouldRenderSerialFields: item.shouldRenderSerialFields,
  };
}

/**
 * A component that renders a searchable item selector with autocomplete.
 *
 * Features:
 * - Real-time search with fuzzy matching across all item types
 * - Automatic fetching of items from the API
 * - Support for creating new items
 * - Sorting that prioritizes active items over archived
 * - Optional filtering by item type, physical status, or archived status
 * - Form integration with error states
 */
function ItemSearchRenderer(props: Props, ref: ForwardedRef<HTMLInputElement>) {
  const { data, isLoading } = useQuery({
    queryKey: [
      'item',
      props.type ?? 'all',
      {
        includeArchived: props.includeArchived,
        excludeLinkedIntegration: props.excludeLinkedIntegration,
      },
    ],
    queryFn: () =>
      getItems({
        type: props.type,
        archived: props.includeArchived,
        limit: 1000,
        excludeLinkedIntegration: props.excludeLinkedIntegration,
      }),
  });

  // Convert API items to SparseItem format
  const allItems = (data?.results ?? []).map(itemToSparseItem);

  const items = getOptions(
    allItems,
    props.filter,
    props.hiddenItems,
    props.includeArchived ?? false,
    props.physicalOnly ?? false
  )
    .slice()
    .sort(compareItems);

  /**
   * Filters and enhances the options based on user input.
   * Adds "create new" option when input doesn't match existing items.
   */
  const filterOptions = (
    options: SparseItem[],
    state: FilterOptionsState<SparseItem>
  ) => {
    const filtered = filter(options, state);
    const inputValue = state.inputValue.trim();

    // Add create new option if there's input, no exact match,
    // and we're not in integration-linking mode
    if (
      !props.excludeLinkedIntegration &&
      inputValue &&
      !filtered.some(
        (item) => item.name.toLowerCase() === inputValue.toLowerCase()
      )
    ) {
      filtered.push({
        id: CREATE_ITEM_OPTION_ID,
        key: CREATE_ITEM_OPTION_ID,
        name: inputValue,
        type: 'BULK',
        sku: '',
        isArchived: false,
        unitOfMeasure: DEFAULT_UNIT_OF_MEASURE,
        hasAssets: false,
        isPhysical: true,
        shouldRenderSerialFields: false,
      });
    }

    return filtered;
  };

  /**
   * Handles changes to the selected value, including:
   * - Direct item selection
   * - Creation of new items
   * - Clearing the selection
   */
  const handleChange = (_: unknown, value: SparseItem | string | null) => {
    if (typeof value === 'string') {
      return openAddItemPage(value);
    }

    if (value === null) {
      return props.onChange(null);
    }

    if (value.id === CREATE_ITEM_OPTION_ID) {
      openAddItemPage(value.name);
      return;
    }

    props.onChange(value);
  };

  return (
    <ItemAutocomplete
      freeSolo
      clearOnBlur
      forcePopupIcon={true}
      ref={ref}
      size="small"
      name={props.name}
      value={props.value}
      error={props.error}
      label={props.label}
      options={items}
      loading={isLoading}
      onChange={handleChange}
      disabled={props.disabled}
      helperText={props.helperText}
      filterOptions={filterOptions}
      loadingText="Loading..."
      popperWidth={props.popperWidth}
      fullWidth={props.fullWidth}
      placeholder={props.placeholderText}
      showSearchAdornment={props.showSearchAdornment}
    />
  );
}

/**
 * A controlled component for searching and selecting items from the Item Catalog.
 * Supports filtering by type (SERVICE, DEVICE, BULK), archived status, and physical status.
 * Wraps the ItemSearchRenderer with ref forwarding for form integration.
 */
export const ItemSearch = forwardRef(ItemSearchRenderer);
